import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Image as ImageIcon, Video, Cpu, Grid, ArrowRight, Sparkles, Clock, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useCredits } from '../hooks/useCredits';
import { Link } from 'react-router-dom';
import { getUserImages } from '../services/gallery';
import { cn } from '../utils';

const ease = [0.22, 1, 0.36, 1];

const features = [
  {
    title: 'Genera Immagini',
    description: 'Modelli avanzati come FLUX e SDXL.',
    icon: ImageIcon,
    href: '/image-generator',
    color: 'from-blue-500/20 to-blue-600/5',
    iconColor: 'text-blue-400',
    border: 'hover:border-blue-500/30'
  },
  {
    title: 'Genera Video',
    description: 'Crea clip cinematografiche da testo.',
    icon: Video,
    href: '/video-generator',
    color: 'from-purple-500/20 to-purple-600/5',
    iconColor: 'text-purple-400',
    border: 'hover:border-purple-500/30'
  },
  {
    title: 'Esplora Modelli',
    description: 'Confronta tutti i modelli disponibili.',
    icon: Cpu,
    href: '/models',
    color: 'from-emerald-500/20 to-emerald-600/5',
    iconColor: 'text-emerald-400',
    border: 'hover:border-emerald-500/30'
  },
  {
    title: 'La Tua Galleria',
    description: 'Gestisci le tue creazioni salvate.',
    icon: Grid,
    href: '/gallery',
    color: 'from-amber-500/20 to-amber-600/5',
    iconColor: 'text-amber-400',
    border: 'hover:border-amber-500/30'
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease } },
};

export default function Dashboard() {
  const { user } = useAuth();
  const { credits } = useCredits();
  const [recentImages, setRecentImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);

  const displayCredits = typeof credits === 'number' && credits > 999999 ? '∞' : credits?.toLocaleString?.() ?? '0';

  useEffect(() => {
    const fetchRecent = async () => {
      try {
        setLoadingImages(true);
        const result = await getUserImages();
        if (result.success && result.images) {
          // Prendi solo le prime 4 immagini
          setRecentImages(result.images.slice(0, 4));
        }
      } catch (error) {
        console.error("Errore recupero immagini recenti", error);
      } finally {
        setLoadingImages(false);
      }
    };
    fetchRecent();
  }, []);

  return (
    <div className="min-h-screen py-8 pb-24">
      <div className="container mx-auto px-4 max-w-6xl space-y-12">

        {/* ── Header & Quick Actions ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-8"
        >
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={16} className="text-blue-400" />
              <span className="text-xs font-bold uppercase tracking-widest text-white/40">
                Workspace
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)', letterSpacing: '-0.03em' }}>
              Bentornato,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-200 to-white/70">
                {user?.username || 'Creativo'}
              </span>
            </h1>
            <p className="text-white/50 text-lg">Pronto a dare vita alle tue idee?</p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/image-generator" className="group">
              <button className="w-full sm:w-auto relative overflow-hidden rounded-2xl p-[1px] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)]">
                <span className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center justify-center gap-2 bg-black/50 backdrop-blur-xl px-8 py-4 rounded-2xl">
                  <ImageIcon size={18} className="text-blue-300" />
                  <span className="font-bold text-white">Nuova Immagine</span>
                </div>
              </button>
            </Link>
            <Link to="/video-generator" className="group">
              <button className="w-full sm:w-auto relative overflow-hidden rounded-2xl p-[1px] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(168,85,247,0.5)]">
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl opacity-40 group-hover:opacity-100 transition-opacity" />
                <div className="relative flex items-center justify-center gap-2 bg-[#0a0a0c] px-8 py-4 rounded-2xl">
                  <Video size={18} className="text-purple-300" />
                  <span className="font-bold text-white">Nuovo Video</span>
                </div>
              </button>
            </Link>
          </div>
        </motion.div>

        {/* ── Stats Overview ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          <div className="panel rounded-3xl p-6 relative overflow-hidden group flex flex-col justify-between">
            <div className="!absolute top-0 right-0 w-32 h-32 bg-yellow-500/10 rounded-full blur-3xl -mr-10 -mt-10 transition-all group-hover:bg-yellow-500/20" />
            <div>
              <div className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-[0.08em] sm:tracking-widest mb-1 sm:mb-2 leading-tight">Crediti</div>
              <div className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>{displayCredits}</div>
            </div>
          </div>
          <div className="panel rounded-3xl p-6 flex flex-col justify-between">
            <div>
              <div className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-[0.08em] sm:tracking-widest mb-1 sm:mb-2 leading-tight">Modelli<br className="sm:hidden" /> Immagine</div>
              <div className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>100+</div>
            </div>
          </div>
          <div className="panel rounded-3xl p-6 flex flex-col justify-between">
            <div>
              <div className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-[0.08em] sm:tracking-widest mb-1 sm:mb-2 leading-tight">Modelli<br className="sm:hidden" /> Video</div>
              <div className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'var(--font-heading)' }}>30+</div>
            </div>
          </div>
          <Link to="/gallery" className="panel rounded-3xl p-6 hover:bg-white/5 transition-colors cursor-pointer group flex flex-col justify-center items-center text-center">
            <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <Grid size={18} className="text-white/70" />
            </div>
            <div className="text-sm font-bold text-white">Vai alla Galleria</div>
          </Link>
        </motion.div>

        {/* ── Recent Generations & Tools ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Recent Generations (Takes up 2 columns on large screens) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, ease }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
                <Clock size={20} className="text-white/40" />
                Creazioni Recenti
              </h2>
              <Link to="/gallery" className="text-sm font-medium text-white/40 hover:text-white flex items-center gap-1 transition-colors">
                Vedi tutte <ChevronRight size={14} />
              </Link>
            </div>

            <div className="panel rounded-[32px] p-2 min-h-[280px]">
              {loadingImages ? (
                <div className="h-full w-full flex items-center justify-center min-h-[260px]">
                  <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                </div>
              ) : recentImages.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {recentImages.map((img, i) => (
                    <motion.div 
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                      key={img.id} 
                      className="group relative aspect-[4/5] rounded-[24px] overflow-hidden bg-black/50"
                    >
                      {img.type === 'video' ? (
                        <video src={img.image_url || img.url} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500" muted loop playsInline />
                      ) : (
                        <img src={img.image_url || img.url} alt={img.prompt} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity duration-500 group-hover:scale-105" />
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                        <p className="text-xs text-white line-clamp-2 font-medium">{img.prompt}</p>
                      </div>
                      <div className="absolute top-3 right-3 p-1.5 rounded-lg bg-black/40 backdrop-blur-md">
                        {img.type === 'video' ? <Video size={12} className="text-white/80" /> : <ImageIcon size={12} className="text-white/80" />}
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="h-full w-full flex flex-col items-center justify-center min-h-[260px] text-center p-8">
                  <div className="w-16 h-16 rounded-3xl bg-white/5 flex items-center justify-center mb-4">
                    <Sparkles size={24} className="text-white/20" />
                  </div>
                  <h3 className="text-white font-bold mb-2">Nessuna creazione</h3>
                  <p className="text-white/40 text-sm max-w-xs mb-6">Non hai ancora generato nulla. Usa gli strumenti qui sopra per iniziare.</p>
                  <Link to="/image-generator" className="pill-ghost px-6 py-2.5 rounded-xl text-sm font-semibold">
                    Genera ora
                  </Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Tools Grid (Takes up 1 column on large screens) */}
          <motion.div 
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6"
          >
            <h2 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              Esplora Strumenti
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-3">
              {features.map((feat) => {
                const Icon = feat.icon;
                return (
                  <motion.div key={feat.title} variants={itemVariants}>
                    <Link to={feat.href} className="block group">
                      <div className={cn(
                        "panel rounded-[24px] p-5 transition-all duration-300 flex items-center gap-4",
                        feat.border
                      )}>
                        <div className={cn(
                          "w-12 h-12 rounded-[18px] flex items-center justify-center shrink-0 bg-gradient-to-br",
                          feat.color
                        )}>
                          <Icon size={20} className={feat.iconColor} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-base font-bold text-white mb-0.5 group-hover:text-blue-400 transition-colors truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                            {feat.title}
                          </h3>
                          <p className="text-xs text-white/40 truncate">
                            {feat.description}
                          </p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center shrink-0 group-hover:bg-white/10 group-hover:-rotate-45 transition-all">
                          <ArrowRight size={14} className="text-white/60 group-hover:text-white" />
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

        </div>
      </div>
    </div>
  );
}
